<template>
  <b-modal ref="modal" size="xl" hide-footer title="Comparison Viewer" modal-class="comparison-modal">
    <div class="d-flex">
      <div class="flex-fill p-3 border-right">
        <h6 class="small text-muted">{{ leftModel.name || 'Model A' }}</h6>
        <div v-if="leftContentExists">
          <field-renderer v-for="(v, k) in leftContent" :key="'l-'+k" :label="k" :value="v" :path="k" side="left" />
        </div>
        <pre v-else class="small">{{ leftRaw }}</pre>
      </div>

      <div class="flex-fill p-3">
        <h6 class="small text-muted">{{ rightModel.name || 'Model B' }}</h6>
        <div v-if="rightContentExists">
          <field-renderer v-for="(v, k) in rightContent" :key="'r-'+k" :label="k" :value="v" :path="k" side="right" />
        </div>
        <pre v-else class="small">{{ rightRaw }}</pre>
      </div>
    </div>
  </b-modal>
</template>

<script>
// Recursive field renderer component
const FieldRenderer = {
  name: 'FieldRenderer',
  props: { label: { type: [String, Number], required: true }, value: { required: true }, path: { type: String, required: true }, side: { type: String, required: true } },
  data() { return { open: true } },
  computed: {
    isArray() { return Array.isArray(this.value) },
    isObject() { return this.value && typeof this.value === 'object' && !Array.isArray(this.value) },
    isPrimitive() { return !this.isArray && !this.isObject }
  },
  methods: {
    toggle() { this.open = !this.open },
    stringify(v) { try { return typeof v === 'string' ? v : JSON.stringify(v, null, 2) } catch (e) { return String(v) } }
  },
  render(h) {
    const header = h('div', { class: 'd-flex justify-content-between align-items-center' }, [
      h('div', [ h('strong', this.label + (this.isPrimitive ? ': ' : '')) , this.isPrimitive ? ' ' + this.stringify(this.value) : null ]),
      this.isObject || this.isArray ? h('div', [ h('b-button', { props: { variant: 'link', size: 'sm' }, on: { click: this.toggle } }, [ this.open ? 'Collapse' : 'Expand' ]) ]) : null
    ])

    let body = null
    if (this.isObject) {
      const children = Object.keys(this.value || {}).map(k => h('field-renderer', { props: { label: k, value: this.value[k], path: this.path + '.' + k, side: this.side } }))
      body = h('b-collapse', { props: { visible: this.open } }, [ h('div', { class: 'mt-2' }, children) ])
    } else if (this.isArray) {
      const items = this.value.map((it, idx) => {
        if (it === null || typeof it === 'undefined' || typeof it === 'string' || typeof it === 'number' || typeof it === 'boolean') {
          return h('div', { class: 'mb-1' }, String(it))
        }
        return h('field-renderer', { props: { label: idx, value: it, path: this.path + '.' + idx, side: this.side } })
      })
      body = h('b-collapse', { props: { visible: this.open } }, [ h('div', { class: 'mt-2' }, items) ])
    }

    return h('div', { class: 'field-group mb-2' }, [ header, body ])
  }
}

// allow recursion
FieldRenderer.components = { 'field-renderer': FieldRenderer }

export default {
  name: 'ComparisonViewer',
  components: { 'field-renderer': FieldRenderer },
  data() { return { leftModel: {}, rightModel: {}, leftRaw: '', rightRaw: '' } },
  computed: {
    leftContent() {
      if (this.leftModel && this.leftModel.normalized && typeof this.leftModel.normalized === 'object') return this.leftModel.normalized
      if (this.leftModel && typeof this.leftModel === 'object') return this.leftModel
      return null
    },
    rightContent() {
      if (this.rightModel && this.rightModel.normalized && typeof this.rightModel.normalized === 'object') return this.rightModel.normalized
      if (this.rightModel && typeof this.rightModel === 'object') return this.rightModel
      return null
    },
    leftContentExists() { return !!this.leftContent && Object.keys(this.leftContent).length > 0 },
    rightContentExists() { return !!this.rightContent && Object.keys(this.rightContent).length > 0 }
  },
  methods: {
    stringify(v) { try { return typeof v === 'string' ? v : JSON.stringify(v, null, 2) } catch (e) { return String(v) } },
    show(job) {
      const m0 = job && job.models && job.models[0] ? job.models[0] : null
      const m1 = job && job.models && job.models[1] ? job.models[1] : null
      this.leftModel = m0 || {}
      this.rightModel = m1 || {}
      this.leftRaw = m0 ? (m0.raw ? this.stringify(m0.raw) : (m0.normalized ? this.stringify(m0.normalized) : '')) : ''
      this.rightRaw = m1 ? (m1.raw ? this.stringify(m1.raw) : (m1.normalized ? this.stringify(m1.normalized) : '')) : ''
      this.$nextTick(() => this.$refs.modal.show())
    }
  }
}
</script>

<style scoped>
.comparison-modal .modal-dialog {
  /* Force dialog to occupy ~80% of viewport and prevent shrinking */
  position: fixed !important;
  top: 10vh !important;
  left: 10vw !important;
  width: 80vw !important;
  max-width: 80vw !important;
  min-width: 60vw !important;
  height: 80vh !important;
  margin: 0 !important;
  pointer-events: auto !important;
  display: block !important;
}
.comparison-modal .modal-content {
  height: 100% !important;
  min-height: 100% !important;
  display: flex;
  flex-direction: column;
}
.comparison-modal .modal-header {
  flex: 0 0 auto !important;
}
.comparison-modal .modal-body {
  /* fill remaining space and scroll if needed */
  flex: 1 1 auto !important;
  height: calc(100% - 56px) !important; /* adjust for header height */
  overflow: auto !important;
  padding: 0.5rem 1rem !important;
}
</style>

